import { NPC_CONFIG } from '../../../shared/content/generated/entities.gen';
import { QUEST_DATABASE } from '../../../shared/content/generated/quests.gen';
import { npcQuestOffers } from './npcQuestOffers';

export type NpcMarkerKind = 'quest-featured' | 'quest-available' | 'quest-active' | 'quest-ready' | 'quest-featured-ready' | 'quest-featured-locked' | 'merchant' | 'trainer' | 'banker';

export interface NpcMarker {
  kind: NpcMarkerKind;
  glyph: '!' | '…' | '?' | '$' | '✦' | '⛁' | '🔒';
  color: number;
  /** Soft halo behind the glyph (featured quests). */
  glow?: number;
}

export interface NpcMarkerPlayerState {
  level: number;
  activeQuests: ReadonlyArray<{ id: string; completed: boolean }>;
  completedQuestIds: ReadonlyArray<string>;
}

export const MARKERS: Record<NpcMarkerKind, NpcMarker> = {
  // Plan 10: a featured quest on offer reads as a bigger moment than an ordinary '!'.
  // D12 (2026-10-07): warm burnished gold, not cream, and clearly apart from the
  // yellow '!' and green '?' (it also draws larger, with an ember halo and a pulse).
  'quest-featured': { kind: 'quest-featured', glyph: '!', color: 0xffa62b, glow: 0xff6a00 },
  'quest-available': { kind: 'quest-available', glyph: '!', color: 0xffcf40 },
  'quest-active': { kind: 'quest-active', glyph: '…', color: 0x61b8ff },
  'quest-ready': { kind: 'quest-ready', glyph: '?', color: 0x55e68a },
  'quest-featured-ready': { kind: 'quest-featured-ready', glyph: '?', color: 0xffa62b, glow: 0xff6a00 },
  'quest-featured-locked': { kind: 'quest-featured-locked', glyph: '🔒', color: 0x4aa3ff, glow: 0x0088ff },
  merchant: { kind: 'merchant', glyph: '$', color: 0x74e0b8 },
  trainer: { kind: 'trainer', glyph: '✦', color: 0xff806b },
  banker: { kind: 'banker', glyph: '⛁', color: 0xa55eea },
};

/**
 * Render-side projection of the authoritative React quest snapshot.
 *
 * The server remains responsible for accepting and turning in quests. This
 * store only computes the marker that should be drawn above each NPC, using
 * the same level/prerequisite/completion rules as NPCInteractionHandler.
 */
export class NpcMarkerStore {
  private markers = new Map<string, NpcMarker[]>();

  public setPlayerState(state: NpcMarkerPlayerState): void {
    const activeById = new Map(state.activeQuests.map((quest) => [quest.id, quest]));
    const completed = new Set(state.completedQuestIds);
    const readyMarkerByNpc = new Map<string, NpcMarker>();
    const activeMarkerByNpc = new Map<string, NpcMarker>();

    // A ready turn-in is the most important action, including quests that
    // began at another NPC and end here.
    for (const activeQuest of state.activeQuests) {
      if (!activeQuest.completed) continue;
      const quest = QUEST_DATABASE[activeQuest.id];
      const endNpc = quest?.endNpc;
      if (endNpc) {
        const marker = quest?.featured ? MARKERS['quest-featured-ready'] : MARKERS['quest-ready'];
        const existing = readyMarkerByNpc.get(endNpc);
        if (!existing || existing.kind !== 'quest-featured-ready') {
          readyMarkerByNpc.set(endNpc, marker);
        }
      }
    }

    // Accepted quests remain visible at their hand-in NPC while work is still
    // in progress. Ready turn-ins above win when several quests share an NPC.
    for (const activeQuest of state.activeQuests) {
      if (activeQuest.completed) continue;
      const endNpc = QUEST_DATABASE[activeQuest.id]?.endNpc;
      if (endNpc && !readyMarkerByNpc.has(endNpc)) {
        activeMarkerByNpc.set(endNpc, MARKERS['quest-active']);
      }
    }

    // Plan 04 B27: If an NPC has an active (in-progress) quest, do not suppress
    // available quest offers. Ready turn-ins still take precedence over new offers.
    const availableMarkerByNpc = new Map<string, NpcMarker>();
    const lockedFeaturedMarkerByNpc = new Map<string, NpcMarker>();
    const offerState = { level: state.level, activeQuestIds: new Set(activeById.keys()), completedQuestIds: completed };
    for (const npc of Object.values(NPC_CONFIG)) {
      if (readyMarkerByNpc.has(npc.id)) continue;

      const { available: availableQuests, hasLockedFeatured } = npcQuestOffers(npc.availableQuests, offerState);

      if (availableQuests.some(questId => QUEST_DATABASE[questId]?.featured)) {
        availableMarkerByNpc.set(npc.id, MARKERS['quest-featured']);
      } else if (availableQuests.length > 0) {
        availableMarkerByNpc.set(npc.id, MARKERS['quest-available']);
      }

      // DJ (October 20): Clearly signal to the player that an NPC holds a locked
      // feature quest (e.g. Elder Wynn holding Beyond the Coil) so they do not skip.
      if (hasLockedFeatured && !availableQuests.some(questId => QUEST_DATABASE[questId]?.featured)) {
        lockedFeaturedMarkerByNpc.set(npc.id, MARKERS['quest-featured-locked']);
      }
    }

    const nextMarkers = new Map<string, NpcMarker[]>();
    for (const npc of Object.values(NPC_CONFIG)) {
      const list: NpcMarker[] = [];
      const readyMarker = readyMarkerByNpc.get(npc.id);
      const availableMarker = availableMarkerByNpc.get(npc.id);
      const lockedFeaturedMarker = lockedFeaturedMarkerByNpc.get(npc.id);
      const activeMarker = activeMarkerByNpc.get(npc.id);

      if (readyMarker) {
        list.push(readyMarker);
      } else {
        if (availableMarker) list.push(availableMarker);
        if (activeMarker) list.push(activeMarker);
        if (lockedFeaturedMarker) list.push(lockedFeaturedMarker);
      }

      if (npc.role === 'trainer' || (npc.trainerClasses && npc.trainerClasses.length > 0)) {
        list.push(MARKERS.trainer);
      }
      if (npc.role === 'shop' || (npc.shopItems && npc.shopItems.length > 0)) {
        list.push(MARKERS.merchant);
      }
      if (npc.role === 'banker') {
        list.push(MARKERS.banker);
      }
      if (list.length > 0) {
        nextMarkers.set(npc.id, list);
      }
    }

    for (const [npcId, marker] of readyMarkerByNpc) {
      if (!nextMarkers.has(npcId)) nextMarkers.set(npcId, [marker]);
    }
    for (const [npcId, marker] of availableMarkerByNpc) {
      if (!nextMarkers.has(npcId)) nextMarkers.set(npcId, [marker]);
    }
    for (const [npcId, marker] of lockedFeaturedMarkerByNpc) {
      if (!nextMarkers.has(npcId)) {
        nextMarkers.set(npcId, [marker]);
      } else {
        const list = nextMarkers.get(npcId)!;
        if (!list.some(m => m.kind === marker.kind)) list.push(marker);
      }
    }
    for (const [npcId, marker] of activeMarkerByNpc) {
      if (!nextMarkers.has(npcId)) {
        nextMarkers.set(npcId, [marker]);
      } else {
        const list = nextMarkers.get(npcId)!;
        if (!list.some(m => m.kind === marker.kind) && !list.some(m => m.kind === 'quest-ready' || m.kind === 'quest-featured-ready')) {
          list.push(marker);
        }
      }
    }

    this.markers = nextMarkers;
  }

  public getMarker(npcId: string): NpcMarker | undefined {
    return this.markers.get(npcId)?.[0];
  }

  public getMarkers(npcId: string): NpcMarker[] {
    return this.markers.get(npcId) ?? [];
  }

  public reset(): void {
    this.markers.clear();
  }
}

export const npcMarkerStore = new NpcMarkerStore();
